'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Link from 'next/link';
import { cn } from '../utils/cn';
import { useLinkConfig, type LinkTarget } from '../providers/LinkProvider';
import { appendUtmToHref, isExternalHref, mergeUtmParams, type UtmParams } from '../providers/utm';
export interface AppLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  /** Destination. String hrefs get UTM handling; internal ones use next/link. */
  href: string;
  /**
   * Per-link UTM params. Merged over (and override) the provider defaults.
   * Shorthand alternative to spelling out `utm={{ source, medium, ... }}`.
   */
  utm?: UtmParams;
  /** Convenience prop for `utm.source`. Wins over `utm.source` if both set. */
  utmSource?: string;
  /** Convenience prop for `utm.medium`. */
  utmMedium?: string;
  /** Convenience prop for `utm.campaign`. */
  utmCampaign?: string;
  /** Convenience prop for `utm.term`. */
  utmTerm?: string;
  /** Convenience prop for `utm.content`. */
  utmContent?: string;
  /** Force-treat the link as external (plain `<a>`), skipping next/link. */
  external?: boolean;
  /** Skip appending any UTM params to this specific link. */
  noUtm?: boolean;
  /** Override the anchor target. Falls back to provider config. */
  target?: LinkTarget;
}

/**
 * A reusable link that:
 * - Renders `next/link` for internal routes and a plain `<a>` for external
 *   URLs (http, mailto, tel, protocol-relative).
 * - Appends UTM params by merging `LinkProvider` global defaults with
 *   per-link props.
 * - Applies sensible `target`/`rel` defaults from the provider, adding
 *   `rel="noopener noreferrer"` to external links opened in a new tab.
 *
 * Existing UTM params already present on the href are never overwritten, and
 * non-http schemes (mailto:/tel:) never receive query params.
 */
export const AppLink = React.forwardRef<HTMLAnchorElement, AppLinkProps>(function AppLink(t0, ref) {
  const $ = _c(48);
  let children;
  let className;
  let external;
  let href;
  let noUtm;
  let rel;
  let rest;
  let target;
  let utm;
  let utmCampaign;
  let utmContent;
  let utmMedium;
  let utmSource;
  let utmTerm;
  if ($[0] !== t0) {
    ({
      href,
      utm,
      utmSource,
      utmMedium,
      utmCampaign,
      utmTerm,
      utmContent,
      external,
      noUtm,
      target,
      rel,
      className,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = external;
    $[4] = href;
    $[5] = noUtm;
    $[6] = rel;
    $[7] = rest;
    $[8] = target;
    $[9] = utm;
    $[10] = utmCampaign;
    $[11] = utmContent;
    $[12] = utmMedium;
    $[13] = utmSource;
    $[14] = utmTerm;
  } else {
    children = $[1];
    className = $[2];
    external = $[3];
    href = $[4];
    noUtm = $[5];
    rel = $[6];
    rest = $[7];
    target = $[8];
    utm = $[9];
    utmCampaign = $[10];
    utmContent = $[11];
    utmMedium = $[12];
    utmSource = $[13];
    utmTerm = $[14];
  }
  const config = useLinkConfig();
  let t1;
  if ($[15] !== external || $[16] !== href) {
    t1 = external ?? isExternalHref(href);
    $[15] = external;
    $[16] = href;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  const isExternal = t1;
  let t2;
  if ($[18] !== config.utm || $[19] !== config.utmExternalOnly || $[20] !== href || $[21] !== isExternal || $[22] !== noUtm || $[23] !== utm || $[24] !== utmCampaign || $[25] !== utmContent || $[26] !== utmMedium || $[27] !== utmSource || $[28] !== utmTerm) {
    const resolvedUtm = mergeUtmParams(config.utm, utm, {
      source: utmSource,
      medium: utmMedium,
      campaign: utmCampaign,
      term: utmTerm,
      content: utmContent
    });
    const shouldApplyUtm = !noUtm && (!config.utmExternalOnly || isExternal) && Object.keys(resolvedUtm).length > 0;
    t2 = shouldApplyUtm ? appendUtmToHref(href, resolvedUtm) : href;
    $[18] = config.utm;
    $[19] = config.utmExternalOnly;
    $[20] = href;
    $[21] = isExternal;
    $[22] = noUtm;
    $[23] = utm;
    $[24] = utmCampaign;
    $[25] = utmContent;
    $[26] = utmMedium;
    $[27] = utmSource;
    $[28] = utmTerm;
    $[29] = t2;
  } else {
    t2 = $[29];
  }
  const finalHref = t2;
  const resolvedTarget = target ?? (isExternal ? config.externalTarget : config.internalTarget);
  const resolvedRel = rel ?? (isExternal && resolvedTarget === "_blank" && config.externalRel ? config.externalRel : undefined);
  let t3;
  if ($[30] !== className) {
    t3 = cn("cursor-pointer", className);
    $[30] = className;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  const mergedClassName = t3;
  if (isExternal) {
    let t4;
    if ($[32] !== children || $[33] !== finalHref || $[34] !== mergedClassName || $[35] !== ref || $[36] !== resolvedRel || $[37] !== resolvedTarget || $[38] !== rest) {
      t4 = <a ref={ref} href={finalHref} target={resolvedTarget} rel={resolvedRel} className={mergedClassName} {...rest}>{children}</a>;
      $[32] = children;
      $[33] = finalHref;
      $[34] = mergedClassName;
      $[35] = ref;
      $[36] = resolvedRel;
      $[37] = resolvedTarget;
      $[38] = rest;
      $[39] = t4;
    } else {
      t4 = $[39];
    }
    return t4;
  }
  const t4 = resolvedTarget === "_self" ? undefined : resolvedTarget;
  let t5;
  if ($[40] !== children || $[41] !== finalHref || $[42] !== mergedClassName || $[43] !== ref || $[44] !== resolvedRel || $[45] !== rest || $[46] !== t4) {
    t5 = <Link ref={ref} href={finalHref} target={t4} rel={resolvedRel} className={mergedClassName} {...rest}>{children}</Link>;
    $[40] = children;
    $[41] = finalHref;
    $[42] = mergedClassName;
    $[43] = ref;
    $[44] = resolvedRel;
    $[45] = rest;
    $[46] = t4;
    $[47] = t5;
  } else {
    t5 = $[47];
  }
  return t5;
});